/**
 * BHADKO OTT - Persistent Storage Manager
 * Handles localStorage sync for channels, favorites, and user preferences.
 */

import { DEFAULT_CHANNELS } from '../data/channels.js';

const CHANNELS_KEY = 'bhadko_ott_channels';
const FAVORITES_KEY = 'bhadko_ott_favorites';
const LANG_KEY = 'bhadko_ott_lang';
const TV_MODE_KEY = 'bhadko_ott_tv_mode';

export class StorageManager {
  /**
   * Initializes channel catalog from localStorage or default seed
   */
  static getChannels() {
    try {
      const stored = localStorage.getItem(CHANNELS_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed;
        }
      }
    } catch (e) {
      console.warn("Error reading channels from storage, using defaults", e);
    }
    
    // Seed default channels
    localStorage.setItem(CHANNELS_KEY, JSON.stringify(DEFAULT_CHANNELS));
    return DEFAULT_CHANNELS;
  }

  static saveChannels(channels) {
    try {
      localStorage.setItem(CHANNELS_KEY, JSON.stringify(channels));
    } catch (e) {
      console.error("Error saving channels to storage", e);
    }
  }

  static getChannelById(id) {
    const channels = this.getChannels();
    return channels.find(c => c.id === id) || null;
  }

  static saveChannel(channelData) {
    const channels = this.getChannels();
    const index = channels.findIndex(c => c.id === channelData.id);
    
    if (index >= 0) {
      channels[index] = { ...channels[index], ...channelData };
    } else {
      channels.push({
        ...channelData,
        id: channelData.id || `custom-${Date.now()}`
      });
    }
    
    this.saveChannels(channels);
    return channels;
  }

  static deleteChannel(id) {
    let channels = this.getChannels();
    channels = channels.filter(c => c.id !== id);
    this.saveChannels(channels);
    
    // Also remove from favorites if present
    this.removeFavorite(id);
    return channels;
  }

  /**
   * Favorites Management
   */
  static getFavorites() {
    try {
      const favs = localStorage.getItem(FAVORITES_KEY);
      return favs ? JSON.parse(favs) : ["bhadko-tv", "bhadko-sports"];
    } catch (e) {
      return ["bhadko-tv", "bhadko-sports"];
    }
  }

  static isFavorite(id) {
    const favorites = this.getFavorites();
    return favorites.includes(id);
  }

  static toggleFavorite(id) {
    let favorites = this.getFavorites();
    const isFav = favorites.includes(id);
    
    if (isFav) {
      favorites = favorites.filter(favId => favId !== id);
    } else {
      favorites.push(id);
    }
    
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(favorites));
    return !isFav;
  }

  static removeFavorite(id) {
    let favorites = this.getFavorites();
    favorites = favorites.filter(favId => favId !== id);
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(favorites));
  }

  /**
   * Language Settings (default: 'gu')
   */
  static getLanguage() {
    return localStorage.getItem(LANG_KEY) || 'gu';
  }

  static setLanguage(lang) {
    localStorage.setItem(LANG_KEY, lang);
  }

  /**
   * TV Mode Settings
   */
  static getTVMode() {
    return localStorage.getItem(TV_MODE_KEY) === 'true';
  }

  static setTVMode(enabled) {
    localStorage.setItem(TV_MODE_KEY, enabled ? 'true' : 'false');
  }
}
